import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'web' | 'ios' | 'android'

export const initCapacitor = async () => {
  if (!isNative) return;

  // Hide splash screen after app loads
  try {
    await SplashScreen.hide();
  } catch {}

  // Configure status bar
  try {
    await StatusBar.setBackgroundColor({ color: '#212A31' });
  } catch {}

  // Handle back button on Android
  if (platform === 'android') {
    CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    });
  }

  // Handle keyboard events
  try {
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
      document.body.classList.add('keyboard-open');
    });
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
      document.body.classList.remove('keyboard-open');
    });
  } catch {}

  // Network status listener
  try {
    Network.addListener('networkStatusChange', (status) => {
      if (!status.connected) {
        document.body.classList.add('offline');
      } else {
        document.body.classList.remove('offline');
      }
    });
  } catch {}
};

// Utility: share content natively
export const nativeShare = async (title, text, url) => {
  if (isNative) {
    const { Share } = await import('@capacitor/share');
    try {
      await Share.share({ title, text, url });
      return true;
    } catch {
      return false;
    }
  }
  // Web fallback
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch {
      return false;
    }
  }
  return false;
};

// Utility: open URL in system browser
export const openInBrowser = async (url) => {
  if (isNative) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
  } else {
    window.open(url, '_blank');
  }
};

// Utility: haptic feedback
export const hapticFeedback = async (type = 'medium') => {
  if (!isNative) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const styles = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: styles[type] || ImpactStyle.Medium });
  } catch {}
};
